"use client";

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { mentorshipApi } from "@/lib/api/services/mentorship.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Users, Clock, CheckCircle } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import Link from "next/link";

interface MentorshipProfile {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  title: string;
  organization: string;
  skills: string[];
  available: boolean;
}

export default function MentorshipPage() {
  const [availableOnly, setAvailableOnly] = useState(false);

  const { data: profiles, isLoading } = useQuery({
    queryKey: ["mentorship-profiles"],
    queryFn: async () => {
      const data = await mentorshipApi.listProfiles();
      return ((data as unknown as { data?: MentorshipProfile[] }).data ?? []) as MentorshipProfile[];
    },
  });

  const filtered = availableOnly ? (profiles ?? []).filter((p) => p.available) : (profiles ?? []);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="space-y-1">
          <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground">Mentorship</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">Find mentors and grow your career.</p>
        </div>
        <Button asChild size="sm">
          <Link href="/mentorship/requests">My Requests</Link>
        </Button>
      </div>

      <label className="flex items-center gap-2 text-sm text-foreground">
        <input type="checkbox" checked={availableOnly} onChange={(e) => setAvailableOnly(e.target.checked)} className="rounded border-border" />
        Available only
      </label>

      {isLoading ? (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}><CardContent className="p-4"><div className="h-4 w-2/3 bg-muted/10 rounded animate-pulse" /><div className="mt-2 h-3 w-1/2 bg-muted/10 rounded animate-pulse" /></CardContent></Card>
          ))}
        </div>
       ) : filtered.length === 0 ? (
         <EmptyState
           variant="default"
           icon={Users}
           title="No mentors found"
           description="Check back later or adjust your filters."
         />
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((profile) => (
            <Card key={profile.id} className="transition-colors hover:border-border/80">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted/10 text-muted-foreground">
                    <Users className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-medium text-foreground truncate">{profile.firstName} {profile.lastName}</h3>
                    <p className="text-xs text-muted-foreground truncate">{profile.title} at {profile.organization}</p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1">
                  {profile.skills.map((skill) => (
                    <span key={skill} className="h-5 px-2 text-[0.6875rem] font-medium rounded-full border bg-muted/10 text-muted-foreground border-border capitalize">{skill}</span>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  {profile.available ? (
                    <span className="h-5 px-2 text-[0.6875rem] font-medium rounded-full border bg-primary/10 text-primary border-primary/20 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Available</span>
                  ) : (
                    <span className="h-5 px-2 text-[0.6875rem] font-medium rounded-full border bg-muted/10 text-muted-foreground border-border flex items-center gap-1"><Clock className="h-3 w-3" /> Busy</span>
                  )}
                </div>
                <Button variant="outline" size="sm" className="mt-3 w-full" asChild>
                  <Link href={`/mentorship`}>View Profile</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
